import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import Helmet from 'react-helmet'; // eslint-disable-line
import * as Validations from '../../../common/form/commonValidations';
import { RenderField, RenderTextareaField, RenderSubmitButton } from '../../../common/form';
import { createRsvpRequest } from '../../../../actions/PublicWebsiteActions';

const isReady = () => typeof window !== 'undefined' && typeof window.grecaptcha !== 'undefined';

class GuestRequestAccess extends Component {
  constructor(props) {
    super(props);
    this.state = {
      guestRequestSubmitted: false,
      recaptchaReady: isReady(),
      recaptchaResponse: null,
      recaptchaCheck: null,
      recaptchaExpired: false,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.recaptchaCallback = this.recaptchaCallback.bind(this);
    this.onRecaptchaExpired = this.onRecaptchaExpired.bind(this);
  }

  componentDidMount() {
    this.initializeRecaptcha();
  }

  componentWillUnmount() {
    clearInterval(this.state.recaptchaCheck);
  }

  onRecaptchaExpired() {
    this.setState({ recaptchaExpired: true });
  }

  handleSubmit() {
    const { onSubmit } = this.props;
    onSubmit();
  }

  initializeRecaptcha() {
    const recaptchaCheck = setInterval(() => {
      if (this.state.recaptchaReady) {
        this.renderRecaptcha(this.state.recaptchaCheck);
      } else {
        this.setState({ recaptchaReady: isReady() });
      }
    }, 1000);
    this.setState({ recaptchaCheck });
    // There's some weird double render issue when you come back to the RSVP page in the same visit
    // This helps get the interval timer cleared
    if (isReady() && this.state.recaptchaReady) {
      this.renderRecaptcha(recaptchaCheck);
    }
  }

  recaptchaCallback(response) {
    this.setState({ recaptchaResponse: response, recaptchaExpired: false });
  }

  renderRecaptcha(intervalId) {
    clearInterval(intervalId);
    const sitekey =
      window.zola.env === 'production'
        ? '6LeioV0UAAAAAPRmqrC2cERXl8k001P_zm7lqcKA'
        : '6Le-kV0UAAAAAPJLDAI2TeBQEuy0oCpRA4mZg8am';
    window.grecaptcha.render('rsvp-request-recaptcha', {
      sitekey,
      callback: this.recaptchaCallback,
      'expired-callback': this.onRecaptchaExpired,
    });
  }

  render() {
    const {
      handleSubmit,
      guestRequestSubmitted,
      sitekey,
      submitting,
      pristine,
      valid,
    } = this.props;
    const notExpired = !this.state.recaptchaExpired;
    return (
      <div>
        {!guestRequestSubmitted ? (
          <div>
            <Helmet>
              <script
                src="https://www.google.com/recaptcha/api.js?initializeRecaptcha=initializeRecaptcha&render=explicit"
                async
                defer
              />
            </Helmet>
            <div id="rsvp-page" className="container request-access-section">
              <div className="request-rsvp-container">
                <div className="col-xs-12 col-md-8 col-md-offset-2">
                  <form onSubmit={handleSubmit} className="request-access-form-v2">
                    <div className="row">
                      <div className="col-xs-12 col-sm-6">
                        <label htmlFor="First Name">First Name</label>
                        <Field
                          name="first_name"
                          id="request-access-firstname"
                          component={RenderField}
                          validate={Validations.REQUIRED}
                          required
                          type="text"
                        />
                      </div>
                      <div className="col-xs-12 col-sm-6">
                        <label htmlFor="Last Name">Last Name</label>
                        <Field
                          name="family_name"
                          id="request-access-lastname"
                          component={RenderField}
                          validate={Validations.REQUIRED}
                          required
                          type="text"
                        />
                      </div>
                      <div className="col-xs-12">
                        <label htmlFor="Email Address">Email Address</label>
                        <Field
                          name="email_address"
                          id="request-access-email"
                          component={RenderField}
                          validate={[Validations.REQUIRED, Validations.EMAIL]}
                          required
                          type="email"
                        />
                      </div>
                      <div className="col-xs-12">
                        <label htmlFor="Message To The Couple (optional)">
                          Note To The Couple (Optional)
                        </label>
                        <Field
                          name="message"
                          id="request-access-message"
                          component={RenderTextareaField}
                          maxChars={250}
                          validate={Validations.MAX_LENGTH_250}
                          type="text"
                          placeholder="Enter names of additional guests listed on your invitation"
                        />
                      </div>
                    </div>
                    <div className="text-center cta-recaptcha-container">
                      <div className="rsvp-request-recaptcha-container">
                        <div
                          className="g-recaptcha"
                          data-sitekey={sitekey}
                          data-callback="handleSubmit"
                          data-size="invisible"
                          id="rsvp-request-recaptcha"
                        />
                      </div>
                    </div>
                    <div className="text-center">
                      <RenderSubmitButton
                        pristine={pristine}
                        submitting={submitting}
                        valid={valid && notExpired}
                        value="Submit Request"
                      />
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div id="rsvp-page" className="container">
            <p className="text-center" style={{ marginTop: '25px' }}>
              Look out for an email from Zola with more information.
            </p>
          </div>
        )}
      </div>
    );
  }
}

GuestRequestAccess.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  guestRequestSubmitted: PropTypes.bool,
  sitekey: PropTypes.string,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  valid: PropTypes.bool,
};

const mapStateToProps = state => ({
  guestSearchResults: state.publicWebsite.guestSearch,
  page: state.publicWebsite.page,
  guestRequestSubmitted: state.publicWebsite.guestRequestSubmitted,
});

const mapDispatchToProps = dispatch => ({
  onSubmit: values => dispatch(createRsvpRequest(values)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: 'GuestLookupForm',
  })(GuestRequestAccess)
);



// WEBPACK FOOTER //
// ./src/components/PublicWebsite/pages/rsvp/GuestRequestAccess.jsx